import { Component, Host, Prop, State, Element, h, Watch } from '@stencil/core';
import { Store } from '../../models/stores/Store';
import { storeService } from '../../utils/services/storeService';
import { filterService } from '../../utils/services/filterService';
import { mapService } from '../../utils/services/mapService';
import { loadingBarService } from '../../utils/services/loadingBarService';
import { ComponentColors, initColors } from '../../utils/constants/colors';
import { searchService } from '../../utils/services/searchService';
import { z } from 'zod';
import { brandService } from '../../utils/services/brandService';
import { ASL_StoreInfoCardProps } from '../aico-store-locator-store-info-card/aico-store-locator-store-info-card';
import { translationService, TranslationsProps } from '../../utils/services/translationService';

export const ASL_ListProps = z.object({
  fetchDistance: z.number().optional(),
  maxDistance: z.number().optional(),
  maxNumberOfStores: z.number().optional(),
  showRecommendedStores: z.boolean().optional(),
  showRecommendedStoresControls: z.boolean().optional(),
  recommendedStoreMaxDistance: z.number().optional(),
  recommendedStoreAllowedBrands: z.array(z.string()).optional(),
  componentColors: ComponentColors.optional(),
  translationsProps: TranslationsProps.optional(),

  ASL_StoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
  ASL_RecommendedStoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
});
export type ASL_ListProps = z.infer<typeof ASL_ListProps>;

@Component({
  tag: 'aico-store-locator-list',
  styleUrl: 'aico-store-locator-list.css',
  shadow: true,
})
export class AicoStoreLocatorList {
  private listRef?: HTMLDivElement;
  private intersectionObserver?: IntersectionObserver;
  private shouldScrollToTop = false;
  private storeElements: Map<number, HTMLElement> = new Map();
  @Prop() isLoading: boolean = false;
  @Prop() fetchDistance: number = 50;
  @Prop() maxDistance: number = 0;
  @Prop() maxNumberOfStores: number = 0;
  @Prop() recommendedStoreMaxDistance?: number;
  @Prop() recommendedStoreAllowedBrands?: string[];
  @Prop() showRecommendedStores: boolean = true;
  @Prop() showRecommendedStoresControls: boolean = true;

  /** Indicates whether the list is attached (used for special logic) */
  @Prop() isAttached: boolean = true;

  /** The center coordinate coming from the search component */
  @Prop() center: google.maps.LatLngLiteral = { lat: 47.3769, lng: 8.5417 };

  /** Selected brands coming from the filter dropdown */
  @State() selectedBrands: number[] = [];
  @State() stores: Store[] = [];
  @State() search: string = '';

  private areRecommendedStoresLoaded: boolean = false;

  @State() carouselIndex: number = 1;
  @State() transitionEnabled: boolean = true;

  @Prop() componentColors?: ComponentColors;
  @Element() el!: HTMLElement;
  @Prop() props?: ASL_ListProps;

  private touchStartX: number = 0;
  private touchEndX: number = 0;

  private unsubscribeController!: AbortController;

  async componentWillLoad() {
    if (this.props) {
      const parsedProps = ASL_ListProps.parse(this.props);
      this.fetchDistance = parsedProps.fetchDistance ?? this.fetchDistance;
      this.maxDistance = parsedProps.maxDistance ?? this.maxDistance;
      this.maxNumberOfStores = parsedProps.maxNumberOfStores ?? this.maxNumberOfStores;
      this.showRecommendedStores = parsedProps.showRecommendedStores ?? this.showRecommendedStores;
      this.showRecommendedStoresControls = parsedProps.showRecommendedStoresControls ?? this.showRecommendedStoresControls;
      this.recommendedStoreMaxDistance = parsedProps.recommendedStoreMaxDistance ?? this.recommendedStoreMaxDistance;
      this.recommendedStoreAllowedBrands = parsedProps.recommendedStoreAllowedBrands ?? this.recommendedStoreAllowedBrands;
      this.componentColors = parsedProps.componentColors ?? this.componentColors;
    }

    await translationService.initComponentLoad('aico-store-locator-list');

    if (this.componentColors) {
      this.componentColors = ComponentColors.parse(this.componentColors);
      initColors(this.el, this.componentColors);
    }

  }

  private t(key: string, locale?: string): string {
    return translationService.getTranslation('aico-store-locator-list', key, locale);
  }

  componentDidLoad() {
    if (this.listRef) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.shouldScrollToTop) {
            this.shouldScrollToTop = false;
            this.listRef?.scrollTo({ top: 0, behavior: 'instant' });
          }
        });
      }, {
        threshold: 0.1,
      });
      this.intersectionObserver.observe(this.listRef);
    }
  }

  @Watch('stores')
  handleStoresChange() {
    if (this.getRecommendedStores().length > 1) {
      this.carouselIndex = 1;
      this.transitionEnabled = true;
    }
  }

  handleItemClick(store: Store) {
    storeService.setSelectedStore(store);
  }

  private filterRecommendedStoreMaxDistance(distance: number): boolean {
    if (!this.recommendedStoreMaxDistance) {
      return true;
    }
    return distance <= this.recommendedStoreMaxDistance;
  }
  private filterRecommendedStoreAllowedBrands(brands: number[] = []): boolean {
    if (!this.recommendedStoreAllowedBrands || this.recommendedStoreAllowedBrands.length === 0) {
      return true;
    }
    return this.recommendedStoreAllowedBrands.some((brandWebName) => {
      return brands.includes(brandService.getIdWithWebName(brandWebName) ?? -1);
    })
  }
  private getRecommendedStores(): Store[] {
    return this.stores.filter((store) => {
      return this.filterRecommendedStoreMaxDistance(store.distance)
        && this.filterRecommendedStoreAllowedBrands(store.crmAddress?.storeLocatorBrandIds);
    }).slice(0, 3);
  }

  private previousRecommendedStore() {
    if (this.extendedStores.length > 1) {
      this.transitionEnabled = true;
      this.carouselIndex = this.carouselIndex - 1;
    }
  }

  private nextRecommendedStore() {
    if (this.extendedStores.length > 1) {
      this.transitionEnabled = true;
      this.carouselIndex = this.carouselIndex + 1;
    }
  }

  // Swipe handlers
  private handleTouchStart(e: TouchEvent) {
    this.touchStartX = e.touches[0].clientX;
  }

  private handleTouchMove(e: TouchEvent) {
    this.touchEndX = e.touches[0].clientX;
  }

  private handleTouchEnd() {
    if (this.touchStartX === 0 || this.touchEndX === 0) return;

    const threshold = 50; // Minimum swipe distance in pixels
    const diff = this.touchStartX - this.touchEndX;

    if (diff > threshold) {
      // Swiped left
      this.nextRecommendedStore();
    } else if (diff < -threshold) {
      // Swiped right
      this.previousRecommendedStore();
    }
    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  // Build extended array for infinite loop carousel when more than one recommended store.
  private get extendedStores(): Store[] {
    const rec = this.search.length > 0 ? this.getRecommendedStores() : [];
    if (rec.length > 1) {
      return [rec[rec.length - 1], ...rec, rec[0]];
    }
    return rec;
  }

  connectedCallback() {
    if (this.unsubscribeController) {
      this.unsubscribeController.abort();
    }
    this.unsubscribeController = new AbortController();

    mapService.subscribeToCenter(
      (center: google.maps.LatLngLiteral) => {
        this.center = center;
        if (!this.isAttached) {
          loadingBarService.setState('loading');
          storeService.fetchStores(center, this.fetchDistance).then(() => {
            this.stores = storeService.getStoresQuery()
              .filterMaxDistance(this.maxDistance)
              .filterWithoutBrands(this.selectedBrands)
              .sortByDistance()
              .filterByMaxCount(this.maxNumberOfStores)
              .get();
            loadingBarService.setState('done');
          });
        }
      },
      this.isAttached,
      { signal: this.unsubscribeController.signal }
    );

    filterService.subscribe(
      (filters: number[]) => {
        this.selectedBrands = filters;
        this.stores = storeService.getStoresQuery()
          .filterMaxDistance(this.maxDistance)
          .filterWithoutBrands(filters)
          .sortByDistance()
          .filterByMaxCount(this.maxNumberOfStores)
          .get();
      },
      { signal: this.unsubscribeController.signal }
    );

    searchService.subscribe(
      (search: string) => {
        this.search = search;
        if (search.length > 0) {
          // Reset carousel index and mark recommended stores as not loaded until StoreService returns.
          this.carouselIndex = this.extendedStores.length > 1 ? 1 : 0;
          this.shouldScrollToTop = true;
          this.areRecommendedStoresLoaded = false;
          this.listRef?.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      { signal: this.unsubscribeController.signal }
    );

    storeService.subscribeToStores(
      () => {
        this.stores = storeService.getStoresQuery()
          .filterMaxDistance(this.maxDistance)
          .filterWithoutBrands(this.selectedBrands)
          .sortByDistance()
          .filterByMaxCount(this.maxNumberOfStores)
          .get();
        this.areRecommendedStoresLoaded = true;
      },
      { signal: this.unsubscribeController.signal }
    );

    storeService.subscribeToSelectedStore(
      (store: Store | undefined) => {
        if (store) {
          this.scrollToStore(store);
        }
      },
      true,
      { signal: this.unsubscribeController.signal }
    );

    loadingBarService.subscribeToStatus((status) => {
      this.isLoading = status === 'loading';
    },
      true,
      { signal: this.unsubscribeController.signal });

    if (!this.isAttached && this.stores.length === 0) {
      loadingBarService.setState('loading');
      storeService.fetchStores(this.center, 50)
        .then(() => loadingBarService.setState('done'));
    }
  }

  disconnectedCallback() {
    this.unsubscribeController.abort();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  private scrollToStore(store: Store) {
    if (!this.listRef) return;
    const element = this.storeElements.get(store.id);
    if (element) {
      const elementOffsetTop = element.offsetTop;
      const elementHeight = element.offsetHeight;
      const containerHeight = this.listRef.clientHeight;
      const scrollPosition = elementOffsetTop - (containerHeight / 2) + (elementHeight / 2);
      this.listRef.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }
  }

  private handleTransitionEnd() {
    // When at the cloned slides, reset the index without animation.
    if (this.extendedStores.length > 1) {
      if (this.carouselIndex === 0) {
        // Jump to last real slide.
        this.transitionEnabled = false;
        this.carouselIndex = this.extendedStores.length - 2;
      } else if (this.carouselIndex === this.extendedStores.length - 1) {
        // Jump to first real slide.
        this.transitionEnabled = false;
        this.carouselIndex = 1;
      }
    }
  }

  render() {

    // const emptyList = this.stores.length === 0;
    // if (emptyList && !this.isLoading) {
    //   return (
    //     <Host class="flex flex-col bg-[--background-light-color] h-full">
    //       <div class="flex flex-1 justify-center items-center text-[--primary-text-color]">
    //         <div class="text-center">
    //           <h2 class="font-semibold text-lg">No Stores Found</h2>
    //           <p>Try adjusting your search or filters.</p>
    //         </div>
    //       </div>
    //     </Host>
    //   );
    // }

    const recStores = this.getRecommendedStores();

    const showCarousel = this.search.length > 0 && recStores.length > 1;
    const showSingle = this.search.length > 0 && recStores.length === 1;
    const transformStyle = { transform: `translateX(-${this.carouselIndex * 100}%)` };

    return (
      <Host class="flex flex-col bg-[--background-light-color] h-full">
        {/* Recommended Section */}
        {this.showRecommendedStores && this.search.length > 0 && (showCarousel || showSingle) && (
          <div class={`pb-2 ${this.showRecommendedStoresControls ? 'px-6' : 'px-2'}`}>
            <h3 class="mb-2 font-semibold text-lg">{this.t('Recommended Stores')}</h3>
            {showCarousel ? (
              <div class="relative">
                {/* Previous button */}
                {this.showRecommendedStoresControls && this.areRecommendedStoresLoaded && (<button
                  class="top-1/2 left-0 z-10 absolute flex justify-center items-center bg-[--primary-button-background-color] shadow-md rounded-full w-10 h-10 text-[--primary-button-text-color] -translate-x-1/2 -translate-y-1/2"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.previousRecommendedStore();
                  }}
                  aria-label="Previous recommended store"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>)}

                {/* Carousel container with swipe support */}
                <div
                  class="overflow-hidden"
                  onTouchStart={(e: TouchEvent) => this.handleTouchStart(e)}
                  onTouchMove={(e: TouchEvent) => this.handleTouchMove(e)}
                  onTouchEnd={() => this.handleTouchEnd()}
                >
                  {this.areRecommendedStoresLoaded ? (
                    <div
                      class={`flex ${this.transitionEnabled ? 'transition-transform duration-300 ease-in-out' : ''}`}
                      style={Object.assign({}, transformStyle)}
                      onTransitionEnd={() => this.handleTransitionEnd()}
                    >
                      {this.extendedStores.map((store) => (
                        <div
                          key={store.id}
                          class="flex-shrink-0 px-1 w-full cursor-pointer"
                          ref={(el) => this.storeElements.set(store.id, el as HTMLElement)}
                          onClick={() => this.handleItemClick(store)}
                        >
                          <aico-store-locator-store-info-card
                            refKey={store.id}
                            organisationName={store.organisationName || 'Unknown Store'}
                            addressLine={store.addressLine1 || ''}
                            zipCode={store.zipCode || ''}
                            city={store.city || ''}
                            distance={store.distance}
                            brandsLogos={brandService.getLogosOrderedByPriority(store.crmAddress?.storeLocatorBrandIds)}
                            class="bg-white p-1 border border-gray-300 rounded-lg"
                            props={this.props?.ASL_RecommendedStoreInfoCardProps || this.props?.ASL_StoreInfoCardProps}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div class="p-1 loading-card" />
                  )}
                </div>

                {/* Next button */}
                {this.showRecommendedStoresControls && this.areRecommendedStoresLoaded && (<button
                  class="top-1/2 right-0 z-10 absolute flex justify-center items-center bg-[--primary-button-background-color] shadow-md rounded-full w-10 h-10 text-[--primary-button-text-color] -translate-y-1/2 translate-x-1/2"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.nextRecommendedStore();
                  }}
                  aria-label="Next recommended store"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>)}

                {/* Indicator dots */}
                {this.areRecommendedStoresLoaded && (<div class="flex justify-center gap-2 mt-2">
                  {recStores.map((_, index) => (
                    <button
                      onClick={() => {
                        this.transitionEnabled = true;
                        this.carouselIndex = index + 1;
                      }}
                      class={`w-2.5 h-2.5 rounded-full transition hover:bg-[--indicator-hover-color] ${index + 1 === this.carouselIndex ? 'bg-[--indicator-selected-color]' : 'bg-[--indicator-default-color]'
                        }`}
                      aria-label={`Go to recommended store ${index + 1}`}
                    />
                  ))}
                </div>)}
              </div>
            ) : (
              <div class="cursor-pointer" onClick={() => this.handleItemClick(recStores[0])}>
                {this.areRecommendedStoresLoaded ? (
                  <aico-store-locator-store-info-card
                    refKey={recStores[0].id}
                    organisationName={recStores[0].organisationName || 'Unknown Store'}
                    addressLine={recStores[0].addressLine1 || ''}
                    zipCode={recStores[0].zipCode || ''}
                    city={recStores[0].city || ''}
                    distance={recStores[0].distance}
                    brandsLogos={brandService.getLogosOrderedByPriority(recStores[0].crmAddress?.storeLocatorBrandIds)}
                    class="bg-white p-1 border border-gray-300 rounded-lg"
                    props={this.props?.ASL_RecommendedStoreInfoCardProps || this.props?.ASL_StoreInfoCardProps}
                  />
                ) : (
                  <div class="p-1 loading-card" />
                )}
              </div>
            )}
          </div>
        )}

        {this.search.length > 0 && recStores.length > 0 && (
          <div class="px-6 py-1">
            <h3 class="font-semibold text-lg">{this.t('All Stores')}</h3>
          </div>
        )}

        <div class="bg-gray-300 h-px" />

        <div
          ref={(el) => (this.listRef = el as HTMLDivElement)}
          class="relative flex-1 [&::-webkit-scrollbar-thumb]:bg-[--scrollbar-thumb-color] [&::-webkit-scrollbar-track]:bg-[--scrollbar-track-color] px-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar]:w-2 overflow-y-auto"
        >
          {this.stores.map((store, index) => (
            <div key={store.id} ref={(el) => this.storeElements.set(store.id, el as HTMLElement)}>
              <aico-store-locator-store-info-card
                refKey={store.id}
                organisationName={store.organisationName || 'Unknown Store'}
                addressLine={store.addressLine1 || ''}
                zipCode={store.zipCode || ''}
                city={store.city || ''}
                distance={store.distance}
                brandsLogos={brandService.getLogosOrderedByPriority(store.crmAddress?.storeLocatorBrandIds)}
                isMobile={!this.isAttached}
                class="cursor-pointer"
                props={this.props?.ASL_StoreInfoCardProps}
                onClick={() => this.handleItemClick(store)}
              />
              {index < this.stores.length - 1 && <div class="bg-gray-300 my-2 h-px" />}
            </div>
          ))}
          {(!this.isAttached && <aico-store-locator-loading-bar />)}
        </div>
      </Host>
    );
  }
}
